import { useEffect, useRef, useState } from "react";
import { Button } from "@electro-planner/ui";
import {
  createDocumentId,
  DEFAULT_DOCUMENT_ID,
  forgetDocument,
  listDocuments,
  type DocumentRecord,
} from "../../lib/documentsRegistry.ts";

type Props = {
  currentId: string;
  currentTitle: string;
  onSwitch: (id: string) => void;
};

/**
 * Dropdown som listar alla dokument som bor i denna browser + knapp för
 * att skapa nytt. Data:n kommer från localStorage-registret; själva Y.Doc
 * lever i IndexedDB per dokument-id.
 */
export function DocumentSwitcher({ currentId, currentTitle, onSwitch }: Props) {
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState<DocumentRecord[]>(() => listDocuments());
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setRecords(listDocuments());
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = currentTitle.trim() || fallbackLabel(currentId);

  return (
    <div ref={wrapperRef} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        title="Byt dokument"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="max-w-[18ch] truncate font-medium">{label}</span>
        <span className="ml-1 text-[var(--color-muted)]">▾</span>
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-full z-30 mt-1 w-80 overflow-hidden rounded-md border border-[var(--color-border)] bg-white shadow-lg"
        >
          <div className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
            Dokument i denna browser
          </div>
          <ul className="max-h-72 overflow-y-auto">
            <DocRow
              id={DEFAULT_DOCUMENT_ID}
              title={
                records.find((r) => r.id === DEFAULT_DOCUMENT_ID)?.title ||
                "Standarddokument"
              }
              lastEditedAt={
                records.find((r) => r.id === DEFAULT_DOCUMENT_ID)?.lastEditedAt
              }
              isCurrent={currentId === DEFAULT_DOCUMENT_ID}
              onOpen={() => {
                setOpen(false);
                onSwitch(DEFAULT_DOCUMENT_ID);
              }}
              onForget={() => {
                // Standarddokumentet går inte att ta bort ur registret —
                // det ligger alltid kvar som fallback.
              }}
              canForget={false}
            />
            {records
              .filter((r) => r.id !== DEFAULT_DOCUMENT_ID)
              .map((r) => (
                <DocRow
                  key={r.id}
                  id={r.id}
                  title={r.title || "(utan namn)"}
                  lastEditedAt={r.lastEditedAt}
                  isCurrent={currentId === r.id}
                  onOpen={() => {
                    setOpen(false);
                    onSwitch(r.id);
                  }}
                  onForget={() => {
                    if (
                      !window.confirm(
                        `Ta bort "${r.title || r.id}" ur listan?\n\nData:n finns kvar i IndexedDB men dokumentet syns inte längre här.`,
                      )
                    )
                      return;
                    forgetDocument(r.id);
                    setRecords(listDocuments());
                  }}
                  canForget={true}
                />
              ))}
          </ul>
          <div className="border-t border-[var(--color-border)] p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={() => {
                setOpen(false);
                onSwitch(createDocumentId());
              }}
            >
              + Nytt dokument
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function fallbackLabel(id: string): string {
  if (id === DEFAULT_DOCUMENT_ID) return "Standarddokument";
  return "(utan namn)";
}

function DocRow({
  title,
  lastEditedAt,
  isCurrent,
  onOpen,
  onForget,
  canForget,
}: {
  id: string;
  title: string;
  lastEditedAt?: string;
  isCurrent: boolean;
  onOpen: () => void;
  onForget: () => void;
  canForget: boolean;
}) {
  return (
    <li
      className={`group flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2 text-sm last:border-b-0 ${
        isCurrent ? "bg-[var(--color-surface)]" : "hover:bg-[var(--color-surface)]"
      }`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 flex-col text-left"
      >
        <span className="truncate font-medium">
          {title}
          {isCurrent ? (
            <span className="ml-2 rounded-sm bg-[var(--color-accent)] px-1 text-[10px] font-semibold text-[var(--color-accent-fg)]">
              öppet
            </span>
          ) : null}
        </span>
        {lastEditedAt ? (
          <span className="text-xs text-[var(--color-muted)]">
            Senast ändrad {formatRelative(lastEditedAt)}
          </span>
        ) : null}
      </button>
      {canForget ? (
        <button
          type="button"
          onClick={onForget}
          title="Ta bort ur listan"
          aria-label="Ta bort dokument ur listan"
          className="rounded p-1 text-xs text-[var(--color-muted)] opacity-0 transition-opacity hover:bg-[var(--color-border)] hover:text-[var(--color-fg)] group-hover:opacity-100 focus-visible:opacity-100"
        >
          ×
        </button>
      ) : null}
    </li>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "nyss";
  if (min < 60) return `${min} min sedan`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h sedan`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d} dag${d === 1 ? "" : "ar"} sedan`;
  return iso.slice(0, 10);
}
